import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import {
  Plus, Search, List, LayoutGrid, Calendar,
  Archive, Trash2, RefreshCw, Edit2,
} from 'lucide-react';
import { DataTable, type ColumnDef } from '../../components/ui/DataTable';
import { toast } from 'sonner';
import { apiGet, apiPost } from '../../api/client';
import type {
  ProjectSummary, ProjectType, ProjectStatus, ProjectPriority,
  ExternalPhotographer, Client, BulkResult,
} from '../../api/types';
import { Button } from '../../components/ui/Button';
import { Input, Select } from '../../components/ui/Input';
import { Modal, ConfirmDialog } from '../../components/ui/Modal';
import { EmptyState } from '../../components/ui/EmptyState';
import { SkeletonRow } from '../../components/ui/Skeleton';
import { BulkActionBar } from '../../components/ui/BulkActionBar';
import { RowActionsMenu } from '../../components/ui/RowActionsMenu';
import { useTableSelection } from '../../hooks/useTableSelection';
import { formatCurrency, formatDate } from '../../utils/format';
import { PageHeader, type ActionDef } from '../../components/ui/PageHeader';

// ─── Label maps ──────────────────────────────────────────────────────────────

const TYPE_LABELS: Record<ProjectType, string> = {
  INTERNO: 'Interno', EVENTO_CORRELATO: 'Evento correlato',
  POST_PRODUZIONE_ESTERNA: 'Post-produzione esterna', ALTRO: 'Altro',
};
const STATUS_LABELS: Record<ProjectStatus, string> = {
  BOZZA: 'Bozza', PREVENTIVATO: 'Preventivato', CONFERMATO: 'Confermato',
  IN_CORSO: 'In corso', IN_ATTESA: 'In attesa', COMPLETATO: 'Completato',
  FATTURATO: 'Fatturato', CHIUSO: 'Chiuso', ANNULLATO: 'Annullato',
};
const PRIORITY_LABELS: Record<ProjectPriority, string> = {
  BASSA: 'Bassa', MEDIA: 'Media', ALTA: 'Alta', URGENTE: 'Urgente',
};
const PRIORITY_COLORS: Record<ProjectPriority, string> = {
  URGENTE: 'var(--color-error)', ALTA: 'var(--color-warning)',
  MEDIA: 'var(--color-primary)', BASSA: 'var(--color-text-faint)',
};
const STATUS_COLORS: Record<ProjectStatus, string> = {
  BOZZA: 'var(--color-text-faint)',
  PREVENTIVATO: 'var(--color-text-muted)',
  CONFERMATO: 'var(--color-success)',
  IN_CORSO: 'var(--color-primary)',
  IN_ATTESA: 'var(--color-warning)',
  COMPLETATO: 'var(--color-gold)',
  FATTURATO: 'var(--color-success)',
  CHIUSO: 'var(--color-text-faint)',
  ANNULLATO: 'var(--color-error)',
};
const KANBAN_STATUSES: ProjectStatus[] = [
  'BOZZA', 'PREVENTIVATO', 'CONFERMATO', 'IN_CORSO', 'IN_ATTESA', 'COMPLETATO',
];

// ─── Inline badges ────────────────────────────────────────────────────────────

const STATUS_SOLID: Record<string, string> = {
  'var(--color-text-faint)': '#6B7280',
  'var(--color-text-muted)': '#9CA3AF',
  'var(--color-success)': '#22C55E',
  'var(--color-primary)': '#3E91FF',
  'var(--color-warning)': '#F59E0B',
  'var(--color-gold)': '#D97706',
  'var(--color-error)': '#EF4444',
};

function StatusBadge({ status }: { status: ProjectStatus }) {
  const cssVar = STATUS_COLORS[status];
  const bg = STATUS_SOLID[cssVar] ?? '#6B7280';
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center',
      padding: '4px 12px',
      borderRadius: 'var(--radius-interactive)',
      fontSize: 12, fontWeight: 600,
      color: '#FFFFFF',
      background: bg,
    }}>
      {STATUS_LABELS[status]}
    </span>
  );
}

function PriorityBadge({ priority }: { priority: ProjectPriority }) {
  return (
    <span style={{ fontSize: 'var(--text-xs)', fontWeight: 600, color: PRIORITY_COLORS[priority] }}>
      {PRIORITY_LABELS[priority]}
    </span>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export default function ProjectsPage() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [view, setView] = useState<'list' | 'kanban'>('list');
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterType, setFilterType] = useState('');
  const [filterPriority, setFilterPriority] = useState('');
  const [page, setPage] = useState(1);
  const [sort, setSort] = useState('updatedAt');
  const [order, setOrder] = useState<'asc' | 'desc'>('desc');
  const [showModal, setShowModal] = useState(false);

  // Bulk state
  const [showBulkStatusModal, setShowBulkStatusModal] = useState(false);
  const [bulkTargetStatus, setBulkTargetStatus] = useState<ProjectStatus>('IN_CORSO');
  const [showBulkArchiveConfirm, setShowBulkArchiveConfirm] = useState(false);
  const [showBulkDeleteConfirm, setShowBulkDeleteConfirm] = useState(false);
  const [showSingleDeleteConfirm, setShowSingleDeleteConfirm] = useState(false);
  const [actionTargetId, setActionTargetId] = useState<string | null>(null);

  const [form, setForm] = useState<{
    title: string; type: ProjectType; priority: ProjectPriority; description: string;
    deadline: string; externalPhotographerId: string; clientId: string;
  }>({ title: '', type: 'INTERNO', priority: 'MEDIA', description: '', deadline: '', externalPhotographerId: '', clientId: '' });

  // Reset selection when filters/page change
  const resetKey = `${search}|${filterStatus}|${filterType}|${filterPriority}|${page}|${sort}|${order}`;
  const sel = useTableSelection(resetKey);

  const handleSort = (field: string) => {
    if (sort === field) setOrder(o => o === 'asc' ? 'desc' : 'asc');
    else { setSort(field); setOrder('asc'); }
    setPage(1);
  };

  const params = new URLSearchParams({ page: String(page), limit: '50', sort, order });
  if (search) params.set('search', search);
  if (filterStatus) params.set('status', filterStatus);
  if (filterType) params.set('type', filterType);
  if (filterPriority) params.set('priority', filterPriority);

  const { data, isLoading } = useQuery<{ data: ProjectSummary[]; meta: { total: number; pages: number; page: number } }>({
    queryKey: ['projects', search, filterStatus, filterType, filterPriority, page, sort, order],
    queryFn: () => apiGet(`/projects?${params}`),
  });

  const { data: extPhotographers } = useQuery<{ data: ExternalPhotographer[] }>({
    queryKey: ['external-photographers-list'],
    queryFn: () => apiGet('/external-photographers?limit=100'),
    enabled: showModal,
  });
  const { data: clientsList } = useQuery<{ data: Client[] }>({
    queryKey: ['clients-list'],
    queryFn: () => apiGet('/clients?limit=100'),
    enabled: showModal,
  });

  const createMutation = useMutation({
    mutationFn: (body: typeof form) => apiPost<ProjectSummary>('/projects', {
      title: body.title, type: body.type, priority: body.priority,
      description: body.description || undefined,
      deadline: body.deadline ? new Date(body.deadline).toISOString() : undefined,
      externalPhotographerId: body.externalPhotographerId || undefined,
      clientId: body.clientId || undefined,
    }),
    onSuccess: (proj: ProjectSummary) => {
      qc.invalidateQueries({ queryKey: ['projects'] });
      setShowModal(false);
      toast.success('Progetto creato');
      navigate(`/projects/${proj.id}`);
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const bulkStatusMutation = useMutation({
    mutationFn: ({ ids, status }: { ids: string[]; status: ProjectStatus }) =>
      apiPost<BulkResult>('/projects/bulk/status', { ids, status }),
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: ['projects'] });
      sel.clearSelection();
      setShowBulkStatusModal(false);
      toast.success(`${res.updated} ${res.updated === 1 ? 'progetto aggiornato' : 'progetti aggiornati'}${res.skipped > 0 ? `, ${res.skipped} saltati` : ''}`);
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const bulkArchiveMutation = useMutation({
    mutationFn: (ids: string[]) => apiPost<BulkResult>('/projects/bulk/archive', { ids }),
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: ['projects'] });
      sel.clearSelection();
      setShowBulkArchiveConfirm(false);
      toast.success(`${res.updated} ${res.updated === 1 ? 'progetto archiviato' : 'progetti archiviati'}`);
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const bulkDeleteMutation = useMutation({
    mutationFn: (ids: string[]) => apiPost<BulkResult>('/projects/bulk/delete', { ids }),
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: ['projects'] });
      sel.clearSelection();
      setShowBulkDeleteConfirm(false);
      toast.success(`${res.updated} ${res.updated === 1 ? 'progetto eliminato' : 'progetti eliminati'}`);
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const singleDeleteMutation = useMutation({
    mutationFn: (id: string) => apiPost('/projects/bulk/delete', { ids: [id] }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['projects'] });
      setShowSingleDeleteConfirm(false);
      setActionTargetId(null);
      toast.success('Progetto eliminato');
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const singleArchiveMutation = useMutation({
    mutationFn: (id: string) => apiPost('/projects/bulk/archive', { ids: [id] }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['projects'] });
      toast.success('Progetto archiviato');
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const projects = data?.data ?? [];
  const selectedIdsList = Array.from(sel.selectedIds);

  const resetFilters = () => {
    setSearch(''); setFilterStatus(''); setFilterType(''); setFilterPriority(''); setPage(1);
  };
  const hasFilters = search || filterStatus || filterType || filterPriority;

  const projectColumns: ColumnDef<ProjectSummary>[] = [
    {
      key: 'icon', label: '',
      render: () => null,
    },
    {
      key: 'name', label: 'Titolo',
      render: (p) => (
        <div>
          <span style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)', fontFamily: 'monospace' }}>{p.code}</span>
          <p style={{ fontWeight: 500, fontSize: 'var(--text-sm)' }}>{p.title}</p>
          {p.client && <p style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)' }}>{p.client.firstName} {p.client.lastName}</p>}
          {p.externalPhotographer && <p style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)' }}>{p.externalPhotographer.studioName ?? `${p.externalPhotographer.firstName} ${p.externalPhotographer.lastName}`}</p>}
        </div>
      ),
    },
    {
      key: 'status', label: 'Stato',
      render: (p) => <StatusBadge status={p.status} />,
    },
    {
      key: 'deadline', label: 'Scadenza',
      render: (p) => (
        <span style={{ fontSize: 'var(--text-xs)', color: p.deadline ? 'var(--color-text-muted)' : 'var(--color-text-faint)', display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
          {p.deadline ? <><Calendar size={11} />{formatDate(p.deadline)}</> : '—'}
        </span>
      ),
    },
    {
      key: 'type', label: 'Tipo',
      render: (p) => <span style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)' }}>{TYPE_LABELS[p.type]}</span>,
    },
    {
      key: 'actions', label: '',
      render: (p) => (
        <div style={{ display: 'flex', gap: '0.25rem', justifyContent: 'flex-end' }} onClick={(e) => e.stopPropagation()}>
          <RowActionsMenu actions={[
            { label: 'Modifica', icon: <Edit2 size={14} />, onClick: () => navigate(`/projects/${p.id}`) },
            { label: 'Archivia', icon: <Archive size={14} />, onClick: () => singleArchiveMutation.mutate(p.id) },
            { label: 'Elimina', icon: <Trash2 size={14} />, variant: 'danger', onClick: () => { setActionTargetId(p.id); setShowSingleDeleteConfirm(true); } },
          ]} />
        </div>
      ),
    },
  ];

  return (
    <div className="page-enter" style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
      <PageHeader
        title="Progetti"
        subtitle={`${data?.meta.total ?? 0} progetti${hasFilters ? ' filtrati' : ' totali'}`}
        actions={[
          { label: 'Lista', icon: <List size={16} />, onClick: () => setView('list'), variant: (view === 'list' ? 'primary' : 'ghost') as ActionDef['variant'] },
          { label: 'Kanban', icon: <LayoutGrid size={16} />, onClick: () => setView('kanban'), variant: (view === 'kanban' ? 'primary' : 'ghost') as ActionDef['variant'] },
          { label: 'Nuovo progetto', icon: <Plus size={16} />, onClick: () => setShowModal(true), variant: 'primary' as ActionDef['variant'] },
        ]}
      />

      {/* Filters */}
      <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', alignItems: 'center' }}>
        <div style={{ position: 'relative', maxWidth: 320, flex: '1 1 200px' }}>
          <Search size={14} style={{ position: 'absolute', left: '0.75rem', top: '50%', transform: 'translateY(-50%)', color: 'var(--color-text-faint)' }} />
          <input type="search" placeholder="Cerca per titolo, codice…" value={search}
            onChange={e => { setSearch(e.target.value); setPage(1); }}
            style={{ background: 'var(--glass-bg-card)', border: '1px solid rgba(255,255,255,0.10)', borderRadius: 'var(--radius-card)', color: 'var(--color-text)', fontSize: 'var(--text-sm)', padding: '0.5rem 0.75rem 0.5rem 2.25rem', width: '100%', outline: 'none' }} />
        </div>
        <select value={filterStatus} onChange={e => { setFilterStatus(e.target.value); setPage(1); }}
          style={{ background: 'var(--glass-bg-card)', border: '1px solid rgba(255,255,255,0.10)', borderRadius: 'var(--radius-card)', color: filterStatus ? 'var(--color-text)' : 'var(--color-text-muted)', fontSize: 'var(--text-sm)', padding: '0.5rem 0.75rem', cursor: 'pointer' }}>
          <option value="">Tutti gli stati</option>
          {(Object.keys(STATUS_LABELS) as ProjectStatus[]).map(s => <option key={s} value={s}>{STATUS_LABELS[s]}</option>)}
        </select>
        <select value={filterType} onChange={e => { setFilterType(e.target.value); setPage(1); }}
          style={{ background: 'var(--glass-bg-card)', border: '1px solid rgba(255,255,255,0.10)', borderRadius: 'var(--radius-card)', color: filterType ? 'var(--color-text)' : 'var(--color-text-muted)', fontSize: 'var(--text-sm)', padding: '0.5rem 0.75rem', cursor: 'pointer' }}>
          <option value="">Tutti i tipi</option>
          {(Object.keys(TYPE_LABELS) as ProjectType[]).map(t => <option key={t} value={t}>{TYPE_LABELS[t]}</option>)}
        </select>
        <select value={filterPriority} onChange={e => { setFilterPriority(e.target.value); setPage(1); }}
          style={{ background: 'var(--glass-bg-card)', border: '1px solid rgba(255,255,255,0.10)', borderRadius: 'var(--radius-card)', color: filterPriority ? 'var(--color-text)' : 'var(--color-text-muted)', fontSize: 'var(--text-sm)', padding: '0.5rem 0.75rem', cursor: 'pointer' }}>
          <option value="">Tutte le priorità</option>
          {(Object.keys(PRIORITY_LABELS) as ProjectPriority[]).map(p => <option key={p} value={p}>{PRIORITY_LABELS[p]}</option>)}
        </select>
        {hasFilters && (
          <Button variant="ghost" size="sm" onClick={resetFilters} icon={<RefreshCw size={13} />}>
            Reset filtri
          </Button>
        )}
      </div>

      {/* Bulk action bar */}
      <BulkActionBar
        count={sel.selectedCount}
        onClear={sel.clearSelection}
        actions={[
          {
            label: 'Cambia stato',
            icon: <RefreshCw size={13} />,
            onClick: () => setShowBulkStatusModal(true),
          },
          {
            label: 'Archivia',
            icon: <Archive size={13} />,
            variant: 'secondary',
            onClick: () => setShowBulkArchiveConfirm(true),
            loading: bulkArchiveMutation.isPending,
          },
          {
            label: 'Elimina',
            icon: <Trash2 size={13} />,
            variant: 'danger',
            onClick: () => setShowBulkDeleteConfirm(true),
            loading: bulkDeleteMutation.isPending,
          },
        ]}
      />

      {/* List view */}
      {view === 'list' ? (
        <>
        {isLoading ? (
          Array.from({ length: 5 }).map((_, i) => <SkeletonRow key={i} />)
        ) : (
        <DataTable
          columns={projectColumns}
          data={projects}
          gridColumns="36px 2fr 1fr 1fr 1fr 44px"
          rowKey={(p) => p.id}
          onRowClick={(p) => navigate(`/projects/${p.id}`)}
          emptyState={
            <EmptyState icon={<Search size={32} />} title="Nessun progetto trovato"
              description={hasFilters ? 'Prova a rimuovere i filtri' : 'Crea il tuo primo progetto'}
              action={<Button onClick={() => setShowModal(true)} size="sm">Nuovo progetto</Button>} />
          }
          sortBy={sort}
          sortOrder={order}
          onSort={handleSort}
          selectable
          selectedIds={sel.selectedIds}
          onSelectionChange={(ids) => sel.setSelectedIds(ids)}
          isLoading={isLoading}
          stickyHeader
          pagination={{ page: data?.meta.page ?? 1, pages: data?.meta.pages ?? 1, total: data?.meta.total, onPageChange: (p) => setPage(p) }}
        />
        )}
        </>
      ) : (
        /* Kanban */
        <div style={{ display: 'flex', gap: '0.75rem', overflowX: 'auto', paddingBottom: '0.5rem' }}>
          {KANBAN_STATUSES.map(status => {
            const colProjects = projects.filter(p => p.status === status);
            return (
              <div key={status} style={{ minWidth: 240, flex: '0 0 240px' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.5rem', padding: '0 0.25rem' }}>
                  <span style={{ fontSize: 'var(--text-xs)', fontWeight: 700, color: STATUS_COLORS[status], textTransform: 'uppercase', letterSpacing: '0.05em' }}>{STATUS_LABELS[status]}</span>
                  <span style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-faint)', background: 'var(--glass-bg-card)', borderRadius: 'var(--radius-full)', padding: '0.125rem 0.375rem' }}>{colProjects.length}</span>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', minHeight: 80 }}>
                  {colProjects.length === 0 && (
                    <div style={{ padding: '1rem', textAlign: 'center', color: 'var(--color-text-faint)', fontSize: 'var(--text-xs)', background: 'var(--glass-bg-card)', border: '1px dashed rgba(255,255,255,0.10)', borderRadius: 'var(--radius-card)' }}>Nessun progetto</div>
                  )}
                  {colProjects.map(proj => (
                    <div key={proj.id} onClick={() => navigate(`/projects/${proj.id}`)}
                      style={{ background: 'var(--glass-bg-card)', borderTop: '1px solid rgba(255,255,255,0.10)', borderRight: '1px solid rgba(255,255,255,0.10)', borderBottom: '1px solid rgba(255,255,255,0.10)', borderLeft: `3px solid ${PRIORITY_COLORS[proj.priority]}`, borderRadius: 'var(--radius-card)', padding: '0.75rem', cursor: 'pointer', transition: 'border-color var(--transition-interactive)' }}
                      onMouseEnter={e => { e.currentTarget.style.borderTopColor = `${PRIORITY_COLORS[proj.priority]}`; e.currentTarget.style.borderRightColor = `${PRIORITY_COLORS[proj.priority]}`; e.currentTarget.style.borderBottomColor = `${PRIORITY_COLORS[proj.priority]}`; }}
                      onMouseLeave={e => { e.currentTarget.style.borderTopColor = 'rgba(255,255,255,0.10)'; e.currentTarget.style.borderRightColor = 'rgba(255,255,255,0.10)'; e.currentTarget.style.borderBottomColor = 'rgba(255,255,255,0.10)'; }}>
                      <p style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-faint)', marginBottom: '0.25rem', fontFamily: 'monospace' }}>{proj.code}</p>
                      <p style={{ fontSize: 'var(--text-sm)', fontWeight: 600, color: 'var(--color-text)', marginBottom: '0.375rem', lineHeight: 1.3 }}>{proj.title}</p>
                      {(proj.client || proj.externalPhotographer) && (
                        <p style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)', marginBottom: '0.375rem' }}>
                          {proj.client ? `${proj.client.firstName} ${proj.client.lastName}` : proj.externalPhotographer ? (proj.externalPhotographer.studioName ?? `${proj.externalPhotographer.firstName} ${proj.externalPhotographer.lastName}`) : ''}
                        </p>
                      )}
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <span style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-faint)' }}>{TYPE_LABELS[proj.type]}</span>
                        {proj.deadline && (
                          <span style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-faint)', display: 'flex', alignItems: 'center', gap: '0.125rem' }}>
                            <Calendar size={10} />{formatDate(proj.deadline)}
                          </span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* ─── Modals ─────────────────────────────────────────────── */}

      {/* New project */}
      <Modal open={showModal} onClose={() => setShowModal(false)} title="Nuovo progetto" width={520}
        footer={
          <>
            <Button variant="ghost" onClick={() => setShowModal(false)}>Annulla</Button>
            <Button onClick={() => createMutation.mutate(form)} loading={createMutation.isPending} disabled={!form.title}>Crea progetto</Button>
          </>
        }>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
          <Input label="Titolo *" value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} required />
          <div className="g2" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
            <Select label="Tipologia *" value={form.type} onChange={e => setForm(f => ({ ...f, type: e.target.value as ProjectType }))}
              options={(Object.keys(TYPE_LABELS) as ProjectType[]).map(t => ({ value: t, label: TYPE_LABELS[t] }))} />
            <Select label="Priorità" value={form.priority} onChange={e => setForm(f => ({ ...f, priority: e.target.value as ProjectPriority }))}
              options={(Object.keys(PRIORITY_LABELS) as ProjectPriority[]).map(p => ({ value: p, label: PRIORITY_LABELS[p] }))} />
          </div>
          <Input label="Scadenza" type="date" value={form.deadline} onChange={e => setForm(f => ({ ...f, deadline: e.target.value }))} />
          {form.type === 'POST_PRODUZIONE_ESTERNA' && (
            <Select label="Esterno" value={form.externalPhotographerId}
              onChange={e => setForm(f => ({ ...f, externalPhotographerId: e.target.value }))}
              placeholder="Nessuno"
              options={(extPhotographers?.data ?? []).map(p => ({ value: p.id, label: p.studioName ?? `${p.firstName} ${p.lastName}` }))} />
          )}
          {(form.type === 'INTERNO' || form.type === 'EVENTO_CORRELATO' || form.type === 'ALTRO') && (
            <Select label="Cliente (opzionale)" value={form.clientId}
              onChange={e => setForm(f => ({ ...f, clientId: e.target.value }))}
              placeholder="Nessuno"
              options={(clientsList?.data ?? []).map(c => ({ value: c.id, label: `${c.firstName} ${c.lastName}` }))} />
          )}
          <Input label="Descrizione" value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} />
        </div>
      </Modal>

      {/* Bulk change status */}
      <Modal open={showBulkStatusModal} onClose={() => setShowBulkStatusModal(false)} title={`Cambia stato — ${sel.selectedCount} progetti`} width={400}
        footer={
          <>
            <Button variant="ghost" onClick={() => setShowBulkStatusModal(false)}>Annulla</Button>
            <Button
              onClick={() => bulkStatusMutation.mutate({ ids: selectedIdsList, status: bulkTargetStatus })}
              loading={bulkStatusMutation.isPending}
            >
              Applica
            </Button>
          </>
        }>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
          <p style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-muted)' }}>
            Scegli il nuovo stato da applicare ai {sel.selectedCount} progetti selezionati.
          </p>
          <Select
            label="Nuovo stato"
            value={bulkTargetStatus}
            onChange={e => setBulkTargetStatus(e.target.value as ProjectStatus)}
            options={(Object.keys(STATUS_LABELS) as ProjectStatus[]).map(s => ({ value: s, label: STATUS_LABELS[s] }))}
          />
        </div>
      </Modal>

      {/* Bulk archive confirm */}
      <ConfirmDialog
        open={showBulkArchiveConfirm}
        onClose={() => setShowBulkArchiveConfirm(false)}
        onConfirm={() => bulkArchiveMutation.mutate(selectedIdsList)}
        loading={bulkArchiveMutation.isPending}
        title={`Archivia ${sel.selectedCount} progetti?`}
        description={`I ${sel.selectedCount} progetti selezionati verranno archiviati e impostati come CHIUSI. L'operazione è reversibile modificando lo stato.`}
        confirmLabel="Archivia"
      />

      {/* Bulk delete confirm */}
      <ConfirmDialog
        open={showBulkDeleteConfirm}
        onClose={() => setShowBulkDeleteConfirm(false)}
        onConfirm={() => bulkDeleteMutation.mutate(selectedIdsList)}
        loading={bulkDeleteMutation.isPending}
        requiresConfirmCheck
        title={`Eliminare ${sel.selectedCount} progetti?`}
        description={`Questa azione è difficilmente reversibile. I progetti fatturati verranno archiviati invece di essere eliminati definitivamente.`}
        confirmLabel="Elimina"
      />

      {/* Single delete confirm */}
      <ConfirmDialog
        open={showSingleDeleteConfirm}
        onClose={() => { setShowSingleDeleteConfirm(false); setActionTargetId(null); }}
        onConfirm={() => actionTargetId && singleDeleteMutation.mutate(actionTargetId)}
        loading={singleDeleteMutation.isPending}
        title="Eliminare il progetto?"
        description="Il progetto verrà eliminato in modo soft. I dati storici rimarranno nel database."
        confirmLabel="Elimina"
      />
    </div>
  );
}
